import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Handshake, Building, MapPin, Users, Star, Mail, Send, Check, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const PartnersPage = () => {
  const [formData, setFormData] = useState({
    organization: '',
    contactName: '',
    email: '',
    phone: '',
    partnershipType: '',
    partnershipLevel: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const partnershipLevels = [
    {
      id: 'bronze',
      name: 'Bronze',
      icon: '🥉',
      color: 'amber',
      benefits: [
        'Logo sur la page partenaires',
        'Mention dans notre newsletter',
        'Badge partenaire sur vos expériences'
      ]
    },
    {
      id: 'silver',
      name: 'Argent',
      icon: '🥈',
      color: 'slate',
      benefits: [
        'Tous les avantages Bronze',
        'Mise en avant sur la page d\'accueil',
        'Publication sur nos réseaux sociaux',
        'Accès prioritaire aux événements'
      ],
      popular: true
    },
    {
      id: 'gold',
      name: 'Or',
      icon: '🥇',
      color: 'yellow',
      benefits: [
        'Tous les avantages Argent',
        'Bannière exclusive sur le site',
        'Co-création de contenus',
        'Accès aux statistiques partagées',
        'Contact direct avec l\'équipe'
      ]
    }
  ];

  const partnerTypes = [
    { id: 'tourism', label: 'Office de tourisme', icon: MapPin },
    { id: 'business', label: 'Entreprise locale', icon: Building },
    { id: 'association', label: 'Association', icon: Users },
    { id: 'media', label: 'Média / Influence', icon: Star }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.organization || !formData.email || !formData.partnershipType || !formData.partnershipLevel) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);
    try {
      // Helper to get API URL - uses current origin in production
      const getApiUrl = () => {
        if (typeof window !== 'undefined' && window.location.origin.includes('xperience-guides.com')) {
          return window.location.origin;
        }
        return process.env.REACT_APP_BACKEND_URL || window.location.origin;
      };
      const API_URL = getApiUrl() + '/api';
      await fetch(`${API_URL}/contact/partnership`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      toast.success('Demande envoyée ! Nous vous recontacterons rapidement.');
      setFormData({
        organization: '',
        contactName: '',
        email: '',
        phone: '',
        partnershipType: '',
        partnershipLevel: '',
        message: ''
      });
    } catch (error) {
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen pt-20 pb-12" data-testid="partners-page">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiM2MzY2ZjEiIGZpbGwtb3BhY2l0eT0iMC4wNCI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iNCIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />
        
        <div className="max-w-4xl mx-auto px-4 py-16 text-center relative">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', duration: 0.6 }}
            className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg"
          >
            <Handshake className="w-12 h-12 text-white" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent"
          >
            Devenez Partenaire
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-xl text-gray-600 max-w-2xl mx-auto"
          >
            Rejoignez notre réseau et contribuez à promouvoir le tourisme local et authentique
          </motion.p>
        </div>
      </div>

      {/* Partnership Levels */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            Niveaux de partenariat
          </h2>
          <p className="text-gray-600">
            Choisissez le niveau qui correspond à votre engagement
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {partnershipLevels.map((level, i) => (
            <motion.button
              key={level.id}
              type="button"
              onClick={() => setFormData({ ...formData, partnershipLevel: level.id })}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 + i * 0.1 }}
              className={`relative bg-white rounded-2xl shadow-lg p-6 border-2 text-left transition-all hover:shadow-xl ${
                formData.partnershipLevel === level.id 
                  ? 'border-indigo-500 ring-2 ring-indigo-200 scale-[1.02]' 
                  : level.popular 
                    ? 'border-indigo-300 hover:border-indigo-400' 
                    : 'border-gray-100 hover:border-indigo-300'
              }`}
              data-testid={`partnership-level-${level.id}`}
            >
              {level.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-indigo-500 text-white text-xs font-bold px-4 py-1 rounded-full">
                  Populaire
                </div>
              )}
              
              {formData.partnershipLevel === level.id && (
                <div className="absolute top-3 right-3 w-6 h-6 bg-indigo-500 rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-white" />
                </div>
              )}
              
              <div className="text-center mb-6">
                <span className="text-5xl block mb-2">{level.icon}</span>
                <h3 className="text-2xl font-bold text-gray-800">{level.name}</h3>
              </div>
              
              <ul className="space-y-3">
                {level.benefits.map((benefit, j) => (
                  <li key={j} className="flex items-start gap-2 text-gray-600">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{benefit}</span>
                  </li>
                ))}
              </ul>
              
              <div className={`mt-4 py-2 text-center rounded-lg transition-colors ${
                formData.partnershipLevel === level.id 
                  ? 'bg-indigo-100 text-indigo-700 font-medium' 
                  : 'bg-gray-50 text-gray-500'
              }`}>
                {formData.partnershipLevel === level.id ? '✓ Sélectionné' : 'Cliquez pour sélectionner'}
              </div>
            </motion.button>
          ))}
        </div>

        {/* Current Partners Section (Placeholder) */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-3xl p-8 mb-16 text-center"
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Ils nous soutiennent
          </h2>
          <p className="text-gray-500 mb-6">
            Rejoignez notre réseau grandissant de partenaires engagés
          </p>
          <div className="flex items-center justify-center gap-8 opacity-30">
            <div className="w-32 h-16 bg-gray-300 rounded-lg flex items-center justify-center text-gray-500 text-sm">
              Votre logo ici
            </div>
            <div className="w-32 h-16 bg-gray-300 rounded-lg flex items-center justify-center text-gray-500 text-sm">
              Votre logo ici
            </div>
            <div className="w-32 h-16 bg-gray-300 rounded-lg flex items-center justify-center text-gray-500 text-sm">
              Votre logo ici
            </div>
          </div>
        </motion.div>

        {/* Contact Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="bg-white rounded-3xl shadow-xl p-8 md:p-12 max-w-2xl mx-auto"
        >
          <div className="text-center mb-8">
            <Mail className="w-12 h-12 text-indigo-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Contactez-nous
            </h2>
            <p className="text-gray-600">
              Discutons de comment nous pouvons collaborer
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Partner Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type de partenariat souhaité *
              </label>
              <div className="grid grid-cols-2 gap-3">
                {partnerTypes.map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, partnershipType: type.id })}
                    className={`p-3 rounded-xl border-2 transition-all flex items-center gap-2 ${
                      formData.partnershipType === type.id
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 hover:border-indigo-300'
                    }`}
                  >
                    <type.icon className={`w-5 h-5 ${
                      formData.partnershipType === type.id ? 'text-indigo-500' : 'text-gray-400'
                    }`} />
                    <span className="text-sm font-medium">{type.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Organisation *
                </label>
                <Input
                  value={formData.organization}
                  onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                  placeholder="Nom de votre organisation"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Personne de contact
                </label>
                <Input
                  value={formData.contactName}
                  onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                  placeholder="Votre nom"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="contact@organisation.com"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Téléphone
                </label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+33 1 23 45 67 89"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Message
              </label>
              <Textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Parlez-nous de votre projet de partenariat..."
                rows={4}
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full py-6 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
              data-testid="partnership-submit-btn"
            >
              <Send className="w-5 h-5 mr-2" />
              {loading ? 'Envoi en cours...' : 'Envoyer ma demande'}
            </Button>
          </form>
        </motion.div>
      </div>
    </main>
  );
};

export default PartnersPage;
